import ConversaLink from './pages/ConversaLink';
import React, { useEffect, Suspense } from "react";
// lazyWithRetry: tenta 3x quando a chunk falha por flutuação de rede.
// Sem isso, 1 timeout deixava a aba quebrada até refresh manual.
import { lazyWithRetry as lazy } from "@/utils/lazyWithRetry";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@radix-ui/react-tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { CACHE_TIMES } from "@/lib/performanceConfig";
import { canManageLeads } from "@/lib/roles-permissions";
import { useUserProfileContext } from "@/contexts/UserProfileContext";

// Componente que corrige URLs malformadas com query strings encodadas no pathname
function URLFixer() {
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    if (location.pathname.includes('%3F') || location.pathname.includes('%26')) {
      const decoded = decodeURIComponent(location.pathname);
      const questionMarkIndex = decoded.indexOf('?');
      
      if (questionMarkIndex !== -1) {
        const path = decoded.substring(0, questionMarkIndex);
        const query = decoded.substring(questionMarkIndex + 1);
        const newUrl = `${path}?${query}`;
        console.log(`[URLFixer] Corrigindo URL: ${location.pathname} -> ${newUrl}`);
        navigate(newUrl, { replace: true });
      }
    }
  }, [location.pathname, navigate]);
  
  return null;
}

// 🚀 Loading spinner para lazy loading
const PageLoader = () => (
  <div className="flex items-center justify-center h-full min-h-[400px]">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

// 🔒 Componente de proteção por role
const RoleProtectedRoute = ({
  children,
  requiredCheck
}: {
  children: React.ReactNode;
  requiredCheck: (role: string | undefined) => boolean;
}) => {
  const { profile, isLoadingProfile } = useUserProfileContext();

  if (isLoadingProfile) return <PageLoader />;

  if (!requiredCheck(profile?.role)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// 🔒 Variante que aceita roles primário OU secundárias (profile.roles[]).
// Usado em rotas onde alguém com role primário 'consultor' mas roles=['gerente']
// também deve ter acesso — caso comum de gestores que também vendem.
const RoleProtectedRouteWithRoles = ({
  children,
  requiredRoles,
  requiredFuncoes = [],
}: {
  children: React.ReactNode;
  requiredRoles: string[];
  requiredFuncoes?: string[];
}) => {
  const { profile, isLoadingProfile } = useUserProfileContext();
  if (isLoadingProfile) return <PageLoader />;
  const has =
    (profile?.role && requiredRoles.includes(profile.role)) ||
    (Array.isArray(profile?.roles) && profile.roles.some((r) => requiredRoles.includes(r))) ||
    (!!profile?.funcao && requiredFuncoes.includes(profile.funcao));
  if (!has) return <Navigate to="/" replace />;
  return <>{children}</>;
};

const RhProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { profile, isLoadingProfile } = useUserProfileContext();
  if (isLoadingProfile) return <PageLoader />;
  const rhManagementRoles = ['master', 'diretor', 'gerente'];
  const hasManagementRole =
    (!!profile?.role && rhManagementRoles.includes(profile.role)) ||
    profile?.roles?.some((role) => rhManagementRoles.includes(role));
  const hasRhFunction = profile?.funcao === 'RH_DEPARTAMENTO_PESSOAL';
  if (!hasManagementRole && !hasRhFunction) return <Navigate to="/" replace />;
  return <>{children}</>;
};

// Avisos (Douglas 15/09): só master, gerente, supervisor, RH e supervisora do backoffice publicam; os demais veem o próprio painel
const AvisosPorPapel = () => {
  const { profile, isLoadingProfile } = useUserProfileContext();
  if (isLoadingProfile) return <PageLoader />;
  const papeis = [profile?.role, ...(Array.isArray(profile?.roles) ? profile.roles : [])];
  const podePublicar =
    papeis.some((r) => r && ['master', 'gerente', 'supervisor'].includes(r)) ||
    ['RH_DEPARTAMENTO_PESSOAL', 'SUPERVISORA_BACKOFFICE'].includes(profile?.funcao || '');
  return podePublicar ? <GestaoAvisos /> : <PainelAvisos />;
};

// 🧹 Configuração global de cache OTIMIZADA para performance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: CACHE_TIMES.GC_TIME,
      staleTime: CACHE_TIMES.SYSTEM_CONFIG,
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
  },
});

import { ThemeProvider } from "next-themes";
import { LeadDetailModalProvider } from "@/contexts/LeadDetailModalContext";
import { PainelPedidoProvider } from "@/contexts/PainelPedidoContext";
import { ConversaPedidoProvider } from '@/contexts/ConversaPedidoContext';
import { UserProfileProvider } from "@/contexts/UserProfileContext";
import { BrandProvider } from "@/contexts/BrandProvider";
import { EconomyModeProvider } from "@/contexts/EconomyModeContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";

import { Layout } from "@/components/Layout";
import { TvLayout } from "@/components/TvLayout";
import { EconomyModeBanner } from "@/components/layout/EconomyModeBanner";
import { HomeRedirect } from "@/components/HomeRedirect";
import { CommandPaletteProvider } from "@/hooks/use-command-palette";
import { CommandPalette } from "@/components/CommandPalette";

// ============================================
// 🚀 LAZY LOADING - Páginas pesadas carregam sob demanda
// ============================================

// Páginas principais - carregamento lazy
const Planilha = lazy(() => import("./pages/Planilha"));
const Kanbans = lazy(() => import("./pages/Kanbans"));
const Triagens = lazy(() => import("./pages/Triagens"));
const MinhaFila = lazy(() => import("./pages/MinhaFila"));
const Tarefas = lazy(() => import("./pages/Tarefas"));
const Comercial = lazy(() => import("./pages/Comercial"));
const Configuracoes = lazy(() => import("./pages/Configuracoes"));
const McpPrime = lazy(() => import("./pages/McpPrime"));
const Dashboards = lazy(() => import("./pages/Dashboards"));

// Removido: BaseDados e Distribuicao - agora usam MasterLeads via query params

// Páginas secundárias - lazy
const Usuarios = lazy(() => import("./pages/Usuarios"));
const Conectar = lazy(() => import("./pages/Conectar"));
const Equipes = lazy(() => import("./pages/Equipes"));
const MinhaPerformanceBko = lazy(() => import("./pages/MinhaPerformanceBko"));
const DashboardGerencial = lazy(() => import("./pages/DashboardGerencial"));
const NotFound = lazy(() => import("./pages/NotFound"));
const MinhasSolicitacoes = lazy(() => import("./pages/MinhasSolicitacoes"));
const BuscaLead360 = lazy(() => import("./pages/BuscaLead360"));
const FaturasConsultor = lazy(() => import("./pages/FaturasConsultor"));
const DiscadoraWrapper = lazy(() => import("./pages/DiscadoraWrapper"));
const DiscadoraMesa = lazy(() => import("./pages/DiscadoraMesa"));
const Cobertura = lazy(() => import("./pages/Cobertura"));
const MailingDiarios = lazy(() => import("./pages/MailingDiarios"));
const BoraFazer = lazy(() => import("./pages/BoraFazer"));

const SLAs = lazy(() => import("./pages/SLAs"));
const EmailMarketing = lazy(() => import("./pages/EmailMarketing"));
const CentralCustosPagina = lazy(() => import("./pages/PaginaUnica").then(m => ({ default: m.CentralCustosPagina })));
const CaixaQualidadePagina = lazy(() => import("./pages/PaginaUnica").then(m => ({ default: m.CaixaQualidadePagina })));
const Gerente = lazy(() => import("./pages/Gerente"));
const ControleDiarioPagina = lazy(() => import("./pages/PaginaUnica").then(m => ({ default: m.ControleDiarioPagina })));
const PerformanceLeadsPagina = lazy(() => import("./pages/PaginaUnica").then(m => ({ default: m.PerformanceLeadsPagina })));
const ConectaIaPagina = lazy(() => import("./pages/PaginaUnica").then(m => ({ default: m.ConectaIaPagina })));
const QualidadeRedirect = lazy(() => import("./pages/PaginaUnica").then(m => ({ default: m.QualidadeRedirect })));
const CicloFaturasPagina = lazy(() => import("./pages/PaginaUnica").then(m => ({ default: m.CicloFaturasPagina })));
const RH = lazy(() => import("./pages/RH"));
const RHFichaExterna = lazy(() => import("./pages/RHFichaExterna"));
const KonnectConversa = lazy(() => import("./pages/KonnectConversa"));
const RHAvaliacaoExperiencia = lazy(() => import("./pages/RHAvaliacaoExperiencia"));
const CarteiraClientes = lazy(() => import("./pages/CarteiraClientes"));
const RegrasComissao = lazy(() => import("./pages/RegrasComissao"));
const VisaoComissoes = lazy(() => import("./pages/VisaoComissoes"));

// TV Dashboards
const TvVendas = lazy(() => import("./pages/tv/TvVendas"));
const TvQualidade = lazy(() => import("./pages/tv/TvQualidade"));
const TvRotator = lazy(() => import("./pages/tv/TvRotator"));
const TvDiscadora = lazy(() => import("./pages/tv/TvDiscadora"));
const GestaoAvisos = lazy(() => import("./pages/GestaoAvisos"));
const PainelAvisos = lazy(() => import("./pages/PainelAvisos"));
const MeuPerfil = lazy(() => import("./pages/MeuPerfil"));
const LeadsCarteiraMeusLeads = lazy(() => import("./pages/LeadsCarteiraMeusLeads"));
// ModoFoco removido (Douglas 10/06) — feature nunca usada.
const FluxoCRM = lazy(() => import("./pages/FluxoCRM"));
const WhatsappInbox = lazy(() => import("./pages/WhatsappInbox"));
const WhatsappInboxPopup = lazy(() => import("./pages/WhatsappInboxPopup"));
const ConexoesWhatsApp = lazy(() => import("./pages/ConexoesWhatsApp"));
const VisaoPrimeMobile = lazy(() => import("./pages/VisaoPrimeMobile"));
const AgendaPrimeMobile = lazy(() => import("./pages/AgendaPrimeMobile"));

// Componentes que precisam de import imediato
import { LeadDetailModalRenderer } from "./components/leads-carteira/LeadDetailModalRenderer";
import AvisosUsuario from "./components/avisos/AvisosUsuario";
import JornadaTrava from "./components/jornada/JornadaTrava";
import AvisoAjudaTarefa from "./components/tarefas/AvisoAjudaTarefa";
import EsqueciSenha from "./pages/EsqueciSenha";
import RedefinirSenha from "./pages/RedefinirSenha";
import Login from "./pages/Login";

const App = () => {
  return (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider
        attribute="class"
        defaultTheme="light"
        enableSystem={false}
        disableTransitionOnChange={false}
      >
        <TooltipProvider>
          <BrowserRouter>
            <URLFixer />
          <CommandPaletteProvider>
          <UserProfileProvider>
          <BrandProvider>
            <EconomyModeProvider>
              <EconomyModeBanner />
              <PainelPedidoProvider>
                <ConversaPedidoProvider>
                  <LeadDetailModalProvider>
                    <ErrorBoundary fallback={(err) => <div className="p-4 text-destructive text-sm">Erro no modal de lead: {err.message}</div>}>
                      <LeadDetailModalRenderer />
                    </ErrorBoundary>
                    <AvisosUsuario />
                    <JornadaTrava />
                    <AvisoAjudaTarefa />
                  <Toaster />
                  <Sonner />
                  <CommandPalette />
                  
                  {/* 🚀 Suspense para lazy loading */}
                  <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                  <Routes>
                  {/* Rotas normais COM Layout e Sidebar */}
                  <Route element={<Layout />}>
                    <Route path="/" element={<HomeRedirect />} />
                    <Route path="/slas" element={<Navigate to="/dashboard-slas" replace />} />
                    
                    {/* Dashboard consolidado com abas */}
                    <Route path="/dashboards" element={<Dashboards />} />
                    
                    {/* Redirects para dashboards (compatibilidade) */}
                    <Route path="/dashboard-gerencial" element={<Navigate to="/dashboards?tab=gerencial" replace />} />
                    <Route path="/dashboard-venda-diaria" element={<Navigate to="/dashboards?tab=venda-diaria" replace />} />
                    <Route path="/metas-dash" element={<Navigate to="/visao-comissoes" replace />} />
                    <Route path="/dashboard-slas" element={<Navigate to="/dashboards?tab=slas" replace />} />
                    
                    {/* Comercial - Página unificada com tabs */}
                    <Route path="/comercial" element={<Comercial />} />
                    
                    {/* Redirects para Comercial com tabs */}
                    <Route path="/leads-carteira/meus-leads" element={<Navigate to="/comercial?tab=prospeccao" replace />} />
                    <Route path="/consultor/faturas" element={<Navigate to="/comercial?tab=faturas" replace />} />
                    
                    {/* Gestão de Desempenho descontinuada em 07/07/2026 — gestão vive no Painel da Gerência */}
                    <Route path="/gestao" element={<Navigate to="/gerente" replace />} />
                    
                    {/* Páginas independentes (ex-abas de Gestão) */}
                    {/* /catalogo virou redirecionamento (18/09/2026): a página saiu, mas
                        link antigo salvo não pode cair em página de erro. */}
                    <Route path="/catalogo" element={<Navigate to="/configuracoes?tab=catalogo" replace />} />
                    <Route path="/cobertura" element={<Cobertura />} />
                    <Route path="/ecossistema-api" element={<Navigate to="/cobertura" replace />} />
                    {/* Mailing Diários RF — antiga aba Painel RF do Ecossistema API. */}
                    <Route path="/mailing-diarios" element={<MailingDiarios />} />
                    <Route path="/email-marketing" element={<EmailMarketing />} />
                    <Route path="/regras-comissao" element={
                      <RoleProtectedRoute requiredCheck={(role) => role === 'master'}>
                        <RegrasComissao />
                      </RoleProtectedRoute>
                    } />
                    <Route path="/produtividade" element={<Navigate to="/gerente" replace />} />
                    
                    {/* Removido: rotas /base-dados e /distribuicao - agora usam /comercial?tab=master-leads&subtab=X */}
                    
                    {/* Outros */}
                    <Route path="/discadora" element={<DiscadoraWrapper />} />
                    {/* /modo-foco REMOVIDO (Douglas 10/06): feature nunca usada. */}

                    {/* Gestão */}
                    <Route path="/produtos" element={<Navigate to="/configuracoes?tab=produtos" replace />} />
                    <Route path="/planilha" element={<Planilha />} />
                    {/* Estornos virou a aba Estorno da Visao Prime (21/08) — redirect p/ links antigos. */}
                    <Route path="/estornos" element={<Navigate to="/dashboards?tab=estorno" replace />} />
                    <Route path="/kanban" element={<Kanbans />} />
                    <Route path="/triagens" element={<Triagens />} />
                    <Route path="/minha-fila" element={<MinhaFila />} />
                    <Route path="/tarefas" element={<Tarefas />} />
                    <Route path="/kanbans/:quadro" element={<Kanbans />} />
                    <Route path="/kanbans" element={<Kanbans />} />
                    <Route path="/usuarios" element={<Usuarios />} />
                    <Route path="/equipes" element={<Equipes />} />
                    <Route path="/minha-meta" element={<Navigate to="/visao-comissoes" replace />} />
                    {/* Cancelados virou a aba Cancelado da Visao Prime (21/08) — a rota fica
                        de pe para avisos e links profundos, e cai na casca unica. */}
                    <Route path="/dashboard-cancelamentos" element={<Navigate to="/dashboards?tab=cancelado" replace />} />
                    <Route path="/minhas-metas" element={<Navigate to="/visao-comissoes" replace />} />
                    <Route path="/minhas-comissoes" element={<Navigate to="/visao-comissoes" replace />} />
                    <Route path="/visao-comissoes" element={<VisaoComissoes />} />
                    <Route path="/minha-performance" element={<MinhaPerformanceBko />} />
                    
                    {/* Configurações */}
                    <Route path="/configuracoes" element={<Configuracoes />} />

                    {/* MCP Prime — quem pergunta ao CRM pelo Claude. Só direção administra. */}
                    <Route path="/mcp" element={
                      <RoleProtectedRoute requiredCheck={(role) => role === 'master' || role === 'diretor'}>
                        <McpPrime />
                      </RoleProtectedRoute>
                    } />

                    <Route path="/prime-tec-ia" element={<Navigate to="/configuracoes" replace />} />

                    {/* Central de Custos — o que o CRM gasta, por serviço, só master */}
                    <Route path="/central-custos" element={
                      <RoleProtectedRoute requiredCheck={(role) => role === 'master'}>
                        <CentralCustosPagina />
                      </RoleProtectedRoute>
                    } />

                    {/* Caixa da Qualidade — master, gerente e supervisora da Qualidade (a rota confere) */}
                    <Route path="/caixa-qualidade" element={<CaixaQualidadePagina />} />

                    {/* Painel da Gerência — AYA, Discadora Online, Indicadores, Campanhas.
                        Liberado pra master/diretor/gerente (incluindo quem tem essas
                        roles como secundárias em profile.roles[]). */}
                    <Route path="/gerente" element={<Gerente />} />
                    <Route path="/controle-diario" element={
                      <RoleProtectedRouteWithRoles requiredRoles={['master', 'diretor', 'gerente', 'supervisor', 'qualidade']}>
                        <ControleDiarioPagina />
                      </RoleProtectedRouteWithRoles>
                    } />
                    <Route path="/aprovacoes" element={<Navigate to="/busca-lead-360?solicitacoes=aprovacoes" replace />} />
                    <Route path="/performance-leads" element={
                      <RoleProtectedRouteWithRoles requiredRoles={['master', 'diretor', 'gerente', 'supervisor', 'consultor']}>
                        <PerformanceLeadsPagina />
                      </RoleProtectedRouteWithRoles>
                    } />
                    <Route path="/conecta-ia" element={
                      <RoleProtectedRouteWithRoles requiredRoles={['master', 'diretor', 'gerente', 'supervisor', 'coordenador']} requiredFuncoes={['SUPERVISORA_BACKOFFICE']}>
                        <ConectaIaPagina />
                      </RoleProtectedRouteWithRoles>
                    } />
                    <Route path="/ciclo-faturas" element={
                      <RoleProtectedRouteWithRoles requiredRoles={['master', 'diretor', 'gerente', 'supervisor', 'qualidade']}>
                        <CicloFaturasPagina />
                      </RoleProtectedRouteWithRoles>
                    } />

                    <Route path="/carteira" element={<CarteiraClientes />} />
                    <Route path="/carteira-clientes" element={<Navigate to="/carteira?visao=gestao&origem=gestor" replace />} />
                    <Route path="/rh" element={
                      <RhProtectedRoute>
                        <RH />
                      </RhProtectedRoute>
                    } />
                    
                    {/* Redirects para página consolidada */}
                    <Route path="/configuracao-obrigatorios" element={<Navigate to="/configuracoes?tab=obrigatorios" replace />} />
                    <Route path="/catalogo-produtos/grupos-obrigatoriedades" element={<Navigate to="/configuracoes?tab=grupos-obrigatorios" replace />} />
                    <Route path="/configuracao-cnpj-token" element={<Navigate to="/configuracoes?tab=cnpj" replace />} />
                    
                    <Route path="/configuracao-status-pedidos" element={<Navigate to="/configuracoes?tab=status-pedidos" replace />} />
                    <Route path="/configuracao/discadora/filas" element={<Navigate to="/configuracoes?tab=filas-discadora" replace />} />
                    <Route path="/configuracao/discadora/motivos" element={<Navigate to="/configuracoes?tab=motivos-discadora" replace />} />
                    
                    {/* Outras rotas de configuração mantidas */}
                    <Route path="/configuracao-motivos-cancelamento" element={<Navigate to="/configuracoes?tab=motivos-cancelamento" replace />} />
                    <Route path="/historico-cancelamentos" element={<Navigate to="/dashboards?tab=cancelado" replace />} />
                    <Route path="/qualidade" element={<QualidadeRedirect />} />
                    <Route path="/minhas-solicitacoes" element={<MinhasSolicitacoes />} />
                    <Route path="/busca-lead-360" element={<BuscaLead360 />} />
                    <Route path="/avisos" element={<AvisosPorPapel />} />
                    <Route path="/painel-avisos" element={<PainelAvisos />} />
                    <Route path="/meu-perfil" element={<MeuPerfil />} />
                     <Route path="/fluxo-crm" element={<FluxoCRM />} />
                     <Route path="/c/:codigo" element={<ConversaLink />} />
                     <Route path="/whatsapp-inbox" element={<WhatsappInbox />} />
                     <Route path="/conexoes-whatsapp" element={
                       <RoleProtectedRouteWithRoles requiredRoles={['master', 'diretor', 'gerente', 'supervisor']}>
                         <ConexoesWhatsApp />
                       </RoleProtectedRouteWithRoles>
                     } />
                     <Route path="/hora-a-hora" element={<Navigate to="/dashboards?tab=hora-a-hora" replace />} />
                     <Route path="/bora-fazer" element={<Suspense fallback={<PageLoader />}><BoraFazer /></Suspense>} />
                  </Route>
                  
                  {/* Rotas TV SEM Layout - Fullscreen */}
                  <Route element={<TvLayout />}>
                    <Route path="/tv/vendas" element={<TvVendas />} />
                    <Route path="/tv/bko" element={<Navigate to="/tv/qualidade" replace />} />
                    <Route path="/tv/qualidade" element={<TvQualidade />} />
                    <Route path="/tv/rotator" element={<TvRotator />} />
                    <Route path="/tv/discadora" element={<TvDiscadora />} />
                    <Route path="/discadora-popup" element={<DiscadoraMesa />} />
                    <Route path="/whatsapp-inbox-popup" element={<Suspense fallback={<PageLoader />}><WhatsappInboxPopup /></Suspense>} />
                    <Route path="/visao-prime-app" element={<Suspense fallback={<PageLoader />}><VisaoPrimeMobile /></Suspense>} />
                    <Route path="/visao-prime-app/" element={<Suspense fallback={<PageLoader />}><VisaoPrimeMobile /></Suspense>} />
                    <Route path="/agenda-prime-app" element={<Suspense fallback={<PageLoader />}><AgendaPrimeMobile /></Suspense>} />
                    <Route path="/agenda-prime-app/" element={<Suspense fallback={<PageLoader />}><AgendaPrimeMobile /></Suspense>} />
                    <Route path="/mesa-prime-app" element={<DiscadoraMesa />} />
                    <Route path="/mesa-prime-app/" element={<DiscadoraMesa />} />
                  </Route>
                  
                  {/* Rotas de autenticação sem layout */}
                  <Route path="/esqueci-senha" element={<EsqueciSenha />} />
                  <Route path="/redefinir-senha" element={<RedefinirSenha />} />
                  <Route path="/login" element={<Login />} />
                  {/* Pagina publica de scan WhatsApp por link — sem login.
                      Acessada pelo consultor via link gerado pelo admin em
                      Editar Usuario → Criar Evolution → Enviar link. */}
                  <Route path="/conectar/:token" element={<Conectar />} />
                  <Route path="/rh/ficha/:token" element={<RHFichaExterna />} />
                  <Route path="/rh/avaliacao/:token" element={<RHAvaliacaoExperiencia />} />
                  {/* A página da Konnect (24/10/2026): o link da ficha abre só a conversa daquela venda, com a senha da Konnect. */}
                  <Route path="/k/:codigo" element={<KonnectConversa />} />
                  
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                  </Routes>
                  </Suspense>
                  </ErrorBoundary>
                  
                </LeadDetailModalProvider>
                </ConversaPedidoProvider>
            </PainelPedidoProvider>
            </EconomyModeProvider>
          </BrandProvider>
          </UserProfileProvider>
          </CommandPaletteProvider>
          </BrowserRouter>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </ErrorBoundary>
  );
};

export default App;
